import { Protected, SignedIn, SignedOut, useAuth } from "@stardeck-customer-apps/project-auth";
import { Link, Outlet, useLocation } from "react-router-dom";

function AdminLogin() {
  const { signIn } = useAuth();

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="max-w-md w-full px-8">
        <div className="text-center mb-8">
          <h1 className="text-[28px] font-bold text-primary mb-2">Admin</h1>
          <p className="text-text-tertiary text-[14px]">Sign in to manage your website content</p>
        </div>
        <button
          onClick={() => signIn("/admin")}
          className="w-full py-3 px-4 bg-primary text-primary-foreground font-bold text-[14px] hover:opacity-90 transition-opacity"
        >
          Sign in with Stardeck
        </button>
        <div className="mt-6 text-center">
          <Link to="/" className="text-[13px] text-text-muted hover:text-primary">
            ← Back to site
          </Link>
        </div>
      </div>
    </div>
  );
}

function AdminNav() {
  const location = useLocation();
  const { signOut, user } = useAuth();

  const navItems = [
    { path: "/admin", label: "Dashboard", mobileLabel: "Home", exact: true },
    { path: "/admin/posts", label: "Posts", mobileLabel: "Posts" },
    { path: "/admin/projects", label: "Projects", mobileLabel: "Projects" },
    { path: "/admin/content", label: "Content", mobileLabel: "Content" },
    { path: "/admin/experience", label: "Experience", mobileLabel: "Exp." },
    { path: "/admin/education", label: "Education", mobileLabel: "Edu." },
    { path: "/admin/social-links", label: "Links", mobileLabel: "Links" },
    { path: "/admin/settings", label: "Settings", mobileLabel: "Settings" },
  ];

  return (
    <header className="border-b border-border-subtle bg-card sticky top-0 z-50">
      {/* Top row - Logo and user actions */}
      <div className="px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
        <div className="flex items-center gap-6 md:gap-8">
          <Link to="/admin" className="text-[15px] font-bold text-primary">
            Admin
          </Link>
          {/* Desktop navigation - hidden on mobile */}
          <nav className="hidden md:flex items-center gap-6">
            {navItems.map((item) => {
              const isActive = item.exact
                ? location.pathname === item.path
                : location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-[13px] tracking-wide transition-colors ${
                    isActive ? "text-primary" : "text-text-secondary hover:text-primary"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
        <div className="flex items-center gap-3 md:gap-4">
          <Link
            to="/"
            className="text-[11px] md:text-[12px] text-text-muted hover:text-primary px-2 py-1 border border-border"
            target="_blank"
          >
            View site
          </Link>
          {/* Hide email on mobile */}
          <span className="hidden md:inline text-[12px] text-text-muted">{user?.email}</span>
          <button
            onClick={() => signOut()}
            className="text-[11px] md:text-[12px] text-text-muted hover:text-primary"
          >
            Sign out
          </button>
        </div>
      </div>
      {/* Mobile tab navigation - visible only on mobile */}
      <nav className="relative md:hidden border-t border-border-subtle">
        {/* Gradient fade on right edge to indicate more content */}
        <div className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-card to-transparent z-10 pointer-events-none" />
        <div className="flex overflow-x-auto scrollbar-hide scroll-smooth">
          {navItems.map((item) => {
            const isActive = item.exact
              ? location.pathname === item.path
              : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex-shrink-0 text-center px-3 py-2.5 text-[11px] tracking-wide transition-colors ${
                  isActive
                    ? "text-primary border-b-2 border-primary"
                    : "text-text-muted hover:text-text-secondary active:text-text-primary"
                }`}
              >
                {item.mobileLabel}
              </Link>
            );
          })}
        </div>
      </nav>
    </header>
  );
}

function AdminContent() {
  return (
    <Protected
      requiredPermission="deployment:admin-panel:write"
      redirectOnUnauthorizedTo="/unauthorized"
    >
      <div className="min-h-screen bg-background">
        <AdminNav />
        <main className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8">
          <Outlet />
        </main>
      </div>
    </Protected>
  );
}

export function AdminLayout() {
  return (
    <>
      <SignedOut>
        <AdminLogin />
      </SignedOut>
      <SignedIn>
        <AdminContent />
      </SignedIn>
    </>
  );
}
